import { Link } from "react-router-dom";
import { ChevronLeft, Mail } from "lucide-react";
import Logo from "../components/Logo";

const PAGES = {
  about: {
    title: "About Us",
    body: [
      {
        h: "Who we are",
        p: "OmniMist Kitchen Craft is a small Indian kitchen-ware brand with one simple idea: cooking should be healthy, quick and mess-free. We make and sell the OmniMist 2-in-1 Glass Oil Sprayer & Dispenser — one strong glass bottle that sprays a fine mist of oil or pours it neatly, so you use up to 75% less oil without losing taste.",
      },
      {
        h: "Why customers trust us",
        p: "Every bottle is food-grade, lead-free borosilicate glass with a leak-proof cap. We pack orders within 24 hours, offer free express shipping, Cash on Delivery, and free replacements for anything damaged in transit.",
      },
      {
        h: "Talk to us",
        p: "We are real people who love hearing from customers. Email us at hello@omnimist.in — we reply within one business day (Monday to Saturday, 10am–7pm IST).",
      },
    ],
  },
  privacy: {
    title: "Privacy Policy",
    body: [
      {
        h: "What we collect",
        p: "When you place an order we collect your name, email address, mobile number and delivery address. We also collect basic website usage data. We never store your card numbers — online payments are handled fully by Razorpay, a PCI-DSS certified payment gateway.",
      },
      {
        h: "How we use it",
        p: "Your details are used only to: confirm and deliver your order, send you order updates by email, verify your phone number with a one-time password (OTP), and provide support. If you subscribe to our newsletter or post a review, we use that information to send you offers or show your review on the site.",
      },
      {
        h: "Who we share it with",
        p: "Only the partners needed to serve you: Razorpay (payments), Twilio (OTP verification), our courier partner Shiprocket (shipping), and Meta (anonymous ad tracking through a standard pixel). We never sell your personal data to anyone.",
      },
      {
        h: "Cookies & tracking",
        list: [
          "Cart items are stored in your browser so your cart survives a refresh.",
          "A standard Meta (Facebook & Instagram) pixel measures visits and purchases to improve our ads.",
          "You can block cookies in your browser — ordering will still work.",
        ],
      },
      {
        h: "Your rights",
        p: "Want your data corrected or deleted? Email hello@omnimist.in and we will do it within 7 working days.",
      },
    ],
  },
  terms: {
    title: "Terms & Conditions",
    body: [
      {
        h: "Ordering",
        p: "Anyone can order from omnimist.store. Every order must have a valid 10-digit Indian mobile number, which we verify with an OTP to prevent fake orders. Placing an order means you agree to these terms.",
      },
      {
        h: "Pricing & coupons",
        p: "The OmniMist 2-in-1 Glass Oil Sprayer & Dispenser (470ml) sells at ₹349, marked down from its MRP of ₹599. Prices include GST where applicable and can change without notice.",
      },
      {
        h: "Payments",
        p: "We accept Cash on Delivery (pay cash or UPI to the courier, no extra fee) and online payments through Razorpay (UPI, cards, net banking, wallets). We never ask for your card details by email, SMS or phone.",
      },
      {
        h: "Product use",
        p: "Use the OmniMist with food-grade liquids only. Keep the cap away from young children. The glass bottle is strong but is still glass — handle it like any glassware.",
      },
      {
        h: "Governing law",
        p: "These terms follow Indian law. Any dispute is subject to the courts of Mumbai, Maharashtra.",
      },
    ],
  },
  shipping: {
    title: "Shipping & Delivery Policy",
    body: [
      {
        h: "Shipping charges",
        p: "Shipping is completely FREE on every order — no minimum, no hidden charges.",
      },
      {
        h: "How fast it reaches you",
        list: [
          "We pack and hand over your order to the courier within 24 hours.",
          "Delivery takes 2–4 business days for most of India.",
          "Remote pin codes can take 5–9 business days.",
          "You get a tracking link by email as soon as your bottle ships.",
        ],
      },
      {
        h: "Cash on Delivery",
        p: "COD is available across India at no extra fee. Keep the exact amount ready — our courier accepts cash or UPI at your door. Every COD order is verified by phone OTP first.",
      },
      {
        h: "Address & phone",
        p: "Please double-check your address and mobile number — the courier calls before delivery. Orders that cannot be delivered after 3 attempts return to us; we will contact you to re-ship.",
      },
    ],
  },
  returns: {
    title: "Cancellation, Return & Refund Policy",
    body: [
      {
        h: "Cancellation",
        p: "Cancel any order free of charge before it ships (within 24 hours of ordering) — just email hello@omnimist.in with your order number. Prepaid cancellations are refunded in full; COD orders are simply cancelled.",
      },
      {
        h: "Returns & replacements",
        p: "Damaged, leaking or wrong item? Send us a photo on WhatsApp within 48 hours of delivery and we ship a free replacement immediately — no return needed. Changed your mind? Cancel free any time before your order ships, straight from your tracking page.",
      },
      {
        h: "Damaged or wrong product",
        p: "If your bottle arrives damaged or isn't what you ordered, send us a photo within 48 hours and we ship a free replacement immediately — no return needed.",
      },
      {
        h: "How refunds work",
        list: [
          "Prepaid orders: refunded to the original payment method within 5–7 business days.",
          "COD orders: refunded to your bank account or UPI ID within 5–7 business days of us receiving the return.",
          "You get a confirmation email the moment your refund is processed.",
        ],
      },
    ],
  },
};

function Block({ b }) {
  return (
    <div className="mb-6">
      <h2 className="mb-2 font-serif text-xl text-charcoal-900">{b.h}</h2>
      {b.p && <p className="text-[15px] leading-relaxed text-charcoal-500">{b.p}</p>}
      {b.list && (
        <ul className="list-inside list-disc space-y-1.5 text-[15px] leading-relaxed text-charcoal-500">
          {b.list.map((li) => (
            <li key={li}>{li}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function LegalPage({ kind }) {
  const page = PAGES[kind] || PAGES.about;
  return (
    <div className="min-h-screen bg-oat-100" data-testid="legal-page">
      <header className="border-b border-charcoal-900/10 bg-oat-50/90 backdrop-blur">
        <div className="mx-auto flex h-[72px] max-w-3xl items-center justify-between px-5">
          <Link to="/" aria-label="Back to store" data-testid="legal-home-link">
            <Logo />
          </Link>
          <Link
            to="/"
            data-testid="legal-back-link"
            className="flex items-center gap-1.5 text-sm text-charcoal-500 transition-colors hover:text-sage-600"
          >
            <ChevronLeft className="h-4 w-4" /> Back to store
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 py-12">
        <div className="rounded-3xl border border-charcoal-900/10 bg-white p-8 md:p-12">
          <h1 className="font-serif text-4xl text-charcoal-900" data-testid="legal-title">
            {page.title}
          </h1>
          <p className="mt-2 text-xs uppercase tracking-[0.18em] text-charcoal-500">
            OmniMist Kitchen Craft · Last updated October 2026
          </p>
          <div className="mt-8">
            {page.body.map((b) => (
              <Block key={b.h} b={b} />
            ))}
          </div>
          <div className="mt-8 flex items-center gap-2 rounded-2xl bg-oat-100 p-5 text-sm text-charcoal-500">
            <Mail className="h-5 w-5 shrink-0 text-sage-600" />
            Questions about this page? Email hello@omnimist.in — we reply within one
            business day.
          </div>
        </div>
      </main>
    </div>
  );
}
